import { useMemo } from "react";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { Tooltip } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import PayCodeSelect from "../../PayCodeSelect";
import { useVisible } from "../../../../../utils/hooks/useVisible";
import { isAddedTimeSheet } from "../helpers";
import { applyTimezone } from "../../../../../utils/helpers/date";
import { calculateRate, calculateRegularRate } from "../../../../../utils/helpers/shift";
import { getMandatoryBreak } from "../../MandatoryBreakWarning/helpers";
import { useAccess } from "../../../../../utils/hooks/access";

export function OvertimeInfoTooltip({ user, column, row, openOvertimeDrawer }) {
    const { t } = useTranslation();
    const { visible, toggle } = useVisible();
    const id = `user-${user.id}-row-${row.id}-${column.id}-pay-code`;
    const appliedRules = row.original.appliedRules || [];
    const isMultipleProfileOvertime = appliedRules.includes('multiple-profile-overtime');

    if (!isMultipleProfileOvertime) {
        return null;
    }

    return (
        <>
            <span id={id} className="ml-2">
                <FontAwesomeIcon
                    className="cursor-pointer text-sm"
                    icon={faInfo}
                    onClick={() => openOvertimeDrawer({ ...user, date: row.original.date })}
                />
            </span>

            <Tooltip
                isOpen={visible}
                toggle={toggle}
                trigger="hover"
                placement="auto"
                target={id}
            >
                <span className="text-sm font-weight-bolder px-2 m-2">
                    {t("calculate-across-multiprofiles")}
                </span>
            </Tooltip>
        </>
    );
}


export const usePayCodeColumn = ({ canEdit, user, isMyTimesheet = false, openMandatoryBreakWarning }) => {
    const { t } = useTranslation();
    const { hasAccess: canViewMultipleProfiles } = useAccess(isMyTimesheet
        ? "timeSheet.canViewMyMultipleProfiles"
        : "timeSheet.canViewMultipleProfiles");

    return useMemo(() => ({
        Header: t("time-code"),
        accessor: "payCode",
        alwaysVisible: true,
        disableFilters: false,
        filter: "text",
        headCustomProps: {},
        Cell: ({ row, cell, column, updateAddedTimeSheet, updateTimeSheet, openOvertimeDrawer }) => {
            const { original } = row;
            const { date, location, payCode, locked, job } = original;
            const disabled = (!isAddedTimeSheet(original) && locked)
                || row.disabled
                || cell.disabled;

            return (
                <>
                    <div>
                        <PayCodeSelect
                            value={payCode}
                            disabled={disabled || !canEdit}
                            companies={user?.companies}
                            payGroup={user?.payGroup}
                            placeholder="--"
                            onChange={(val) => {
                                const data = { payCode: val };
                                let rate = 0;
                                if (val?.hourType === "amount") {
                                    rate = parseFloat(`${val?.defaultRate || 0}`) || 0;
                                } else if (payCode?.hourType === "amount") {
                                    rate = calculateRate({ ...original, ...data, rateChangeManually: false }, data?.payCode?.hourType === "regular");
                                } else if (val && job) {
                                    let rateChangeManually = original.rateChangeManually;
                                    // fix for weighted rate, change of unpaid break to paid break
                                    if (
                                        original.payCode?.hourType === "unpaid"
                                        && original.rate === 0
                                    ) {
                                        rateChangeManually = false;
                                    }
                                    rate = calculateRate({ ...original, ...data, rateChangeManually }, data?.payCode?.hourType === "regular");
                                }
                                data.rate = rate;
                                data.regRate = calculateRegularRate({ rate, payCode: val });

                                const addedTimesheet = isAddedTimeSheet(original);
                                if (addedTimesheet && ["amount", "unit"].includes(val?.hourType)) {
                                    const timeZone = location?.timezoneValue;
                                    let baseDate = applyTimezone(moment(date), timeZone, true)
                                        .startOf("date")
                                        .toISOString(true);
                                    data.startDate = baseDate;
                                    data.endDate = baseDate;
                                    if (val?.hourType === "unit") {
                                        data.duration = 0;
                                    }
                                }

                                const changeValue = (data) => {
                                    if (addedTimesheet) {
                                        updateAddedTimeSheet(original, data);
                                    } else {
                                        updateTimeSheet(original.id, data);
                                    }
                                }

                                const mandatoryBreak = openMandatoryBreakWarning ? getMandatoryBreak(original) : null;
                                const isMandatoryBreak = !!mandatoryBreak && val?.id === mandatoryBreak.payCode?.id;
                                const duration = moment(data?.endDate || original?.endDate).diff(moment(data?.startDate || original?.startDate), "seconds");
                                if (isMandatoryBreak && Number(data?.duration || duration) < Number(mandatoryBreak.duration) * 60) {
                                    openMandatoryBreakWarning({
                                        column: "pay-code-with-duration",
                                        requiredTime: mandatoryBreak.duration,
                                        original,
                                        updatedFields: data,
                                        onConfirm: () => changeValue(data),
                                        onCancel: () => { }
                                    });
                                } else if (original?.payCode && original?.payCode?.id === mandatoryBreak?.payCode?.id && val && !isMandatoryBreak) {
                                    openMandatoryBreakWarning({
                                        column: "pay-code",
                                        original,
                                        updatedFields: data,
                                        onConfirm: () => changeValue(data),
                                        onCancel: () => { }
                                    })
                                } else {
                                    changeValue(data)
                                }
                            }}
                        />
                        {/**TODO: (original?.appliedRules || []).includes("") */}
                        {(canViewMultipleProfiles && user?.otherProfileIds?.length > 0) && (
                            <OvertimeInfoTooltip
                                user={user}
                                row={row}
                                column={column}
                                openOvertimeDrawer={openOvertimeDrawer}
                            />
                        )}
                    </div>
                </>
            );
        },
    }), [t, canEdit, user, canViewMultipleProfiles, openMandatoryBreakWarning]);
};
